/*
  TGEX cart-drawer.js - complete replacement
  Original MLVeda cart behavior preserved.
  Preserve the previously verified scroll-position fix.
  Remove the retired custom-header-top-bar section from quantity updates
  to prevent an error after the cart has successfully updated.
*/

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // An animation sometimes needs a timeout to trigger reliably.
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
    // Added by MLVeda: start
    document.body.classList.add('cart-open');
    // Added by MLVeda: end
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    // Added by MLVeda: start
    document.body.classList.remove('cart-open');
    // Added by MLVeda: end
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  // Commented by MLVeda: start
  // renderContents(parsedState) {
  //   this.querySelector('.drawer__inner').classList.contains('is-empty') &&
  //     this.querySelector('.drawer__inner').classList.remove('is-empty');
  //   this.productId = parsedState.id;
  //   this.getSectionsToRender().forEach((section) => {
  //     const sectionElement = section.selector
  //       ? document.querySelector(section.selector)
  //       : document.getElementById(section.id);
  //     if (!sectionElement) return;
  //     sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
  //     if (window.location.pathname !== '/cart') {
  //       minMaxify.handleCartUpdate();
  //     }
  //   });
  // Commented by MLVeda: end

  // Added by MLVeda: start
  renderContents(parsedState, needToRender = true) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      if (needToRender) {
        sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      } else {
        needToRender = false;
      }

      document.querySelectorAll('.quantity__input').forEach((input) => {
        handleQuantity(input);
      });
      updateCartBubble();

      if (window.location.pathname !== '/cart') {
        minMaxify.handleCartUpdate();
        const emptyDrawer = document.querySelector('.drawer__inner-empty');
        if (emptyDrawer) {
          emptyDrawer.style.display = 'none';
        }
      }
    });
    // Added by MLVeda: end

    let parser = new DOMParser();
    let cartbubble = parser.parseFromString(parsedState.sections['cart-icon-bubble'], 'text/html');
    // document.querySelector('#cart-count-custom').innerText = cartbubble.querySelector('.cart-count-bubble span[aria-hidden]').innerText;

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  /*
    TGEX: Updating quantities replaces the cart's HTML. That can reset the
    scrollable container to the top. Save the current scroll positions,
    let the original CartItems update run, then restore the positions.
    This does NOT modify the request, quantities, totals, or cart contents.
  */
  updateQuantity(line, quantity, name) {
    const drawer = this.closest('cart-drawer');
    const drawerInner = drawer && drawer.querySelector('.drawer__inner');

    if (!drawer || !drawerInner) {
      return super.updateQuantity(line, quantity, name);
    }

    const targets = [
      { selector: null, element: drawer },
      { selector: '.drawer__inner', element: drawerInner },
      { selector: '#CartDrawer', element: drawer.querySelector('#CartDrawer') },
      { selector: '.drawer__contents', element: drawer.querySelector('.drawer__contents') },
      { selector: 'cart-drawer-items', element: drawer.querySelector('cart-drawer-items') },
      { selector: '.cart-drawer__items', element: drawer.querySelector('.cart-drawer__items') },
    ];
    const savedPositions = targets
      .filter((target) => target.element && target.element.scrollTop > 0)
      .map((target) => ({ selector: target.selector, top: target.element.scrollTop }));

    if (savedPositions.length === 0) {
      return super.updateQuantity(line, quantity, name);
    }

    const restoreScroll = () => {
      for (const saved of savedPositions) {
        const current = saved.selector ? drawer.querySelector(saved.selector) : drawer;
        if (current) current.scrollTop = saved.top;
      }
    };

    let timeoutId;
    const observer = new MutationObserver((mutations) => {
      // The original cart.js replaces the contents of .drawer__inner
      // after Shopify returns the updated cart. Wait for that replacement.
      const contentWasReplaced = mutations.some((mutation) =>
        mutation.type === 'childList' && mutation.target === drawerInner
      );
      if (!contentWasReplaced) return;

      observer.disconnect();
      clearTimeout(timeoutId);
      requestAnimationFrame(() => {
        restoreScroll();
        // The original theme can refocus the quantity input after rendering.
        requestAnimationFrame(restoreScroll);
      });
      setTimeout(restoreScroll, 150);
    });

    observer.observe(drawerInner, { childList: true });
    // Disconnect if the cart update fails or no new markup arrives.
    timeoutId = setTimeout(() => observer.disconnect(), 8000);

    try {
      return super.updateQuantity(line, quantity, name);
    } catch (error) {
      observer.disconnect();
      clearTimeout(timeoutId);
      throw error;
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      // TGEX: Do not request the retired custom-header-top-bar section.
      // The refreshed header uses the cart-icon-bubble above.
      // Keeping the old section in this list can throw during a quantity
      // update after Shopify has already successfully changed the cart.
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);